'use client';

import { useEffect, useMemo, useState } from 'react';
import { PlusCircleIcon, PlusIcon, XIcon } from 'lucide-react';
import isEqual from 'react-fast-compare';
import { toast } from 'sonner';

import { MultiSelectContent } from '@/components/atoms/multi-select';
import { Popover } from '@/components/atoms/popover';
import { Select } from '@/components/atoms/select';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { SKILL_ACTION, SKILL_CATEGORY, SkillAction, SkillCategory } from '@/consts/enums';
import { cn } from '@/lib/utils';

type ApiSkill = ApiSchemas['SkillReadComplete'];

export function CreateSkillDialog() {
  const [open, setOpen] = useState(false);
  const { api } = useApiV1Ctx();
  const { officeId } = useOfficeCtx();
  const {
    office: { issues },
  } = useOfficeCtx();

  const { mutate, isPending } = api.skills.createSkillsPost.useMutation(
    {},
    {
      onSuccess: () => {
        toast.success('Skill created');
        api.skills.getAllSkillsGet.invalidateQueries();
        setOpen(false);
      },
    }
  );

  const [values, setValues] = useState<SkillFormValues>({
    name: '',
    description: '',
    category: null,
    action: null,
    issue_ids: [],
  });

  const onChange = (values: Partial<SkillFormValues>) => {
    setValues((prev) => ({ ...prev, ...values }));
  };

  const onSubmit = () => {
    if (!values.name) {
      toast.error('Name is required');
      return;
    }

    mutate({
      office_id: officeId,
      name: values.name,
      description: values.description || null,
      category: values.category,
      action: values.action,
      issue_ids: values.issue_ids,
      status: 'ACTIVE',
    });
  };

  const pestOptions = issues.map((pest) => ({
    value: pest.id,
    label: pest.name,
  }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size='sm'>
          <PlusIcon className='size-4' />
          Add Skill
        </Button>
      </DialogTrigger>
      <DialogContent showClose className='max-w-2xl' id='skills-dialog'>
        <DialogHeader>
          <DialogTitle>Add Skill</DialogTitle>
          <DialogDescription>Add a new skill to your office.</DialogDescription>
        </DialogHeader>
        <SkillFormInputs className='py-4' values={values} onChange={onChange} pestOptions={pestOptions} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button type='submit' onClick={onSubmit} disabled={!values.name || isPending}>
            {isPending ? 'Creating...' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface EditSkillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skill: ApiSkill;
}

export function EditSkillDialog({ open, onOpenChange, skill }: EditSkillDialogProps) {
  const { api } = useApiV1Ctx();
  const {
    office: { issues },
  } = useOfficeCtx();

  const { mutate, isPending } = api.skills.updateSkillsSkillIdPatch.useMutation(
    {
      path: { skill_id: skill.id },
    },
    {
      onSuccess: () => {
        toast.success('Skill updated');
        api.skills.getAllSkillsGet.invalidateQueries();
        onOpenChange(false);
      },
    }
  );

  const [values, setValues] = useState<SkillFormValues>({
    name: skill.name,
    description: skill.description || '',
    category: skill.category || null,
    action: skill.action || null,
    issue_ids: skill.skill_issues?.map((sp) => sp.issue_id) || [],
  });

  const isDisabled = useMemo(() => {
    if (!values.name) return true;
    if (isPending) return true;
    return isEqual(values, {
      name: skill.name,
      description: skill.description || '',
      category: skill.category || null,
      action: skill.action || null,
      issue_ids: skill.skill_issues?.map((sp) => sp.issue_id) || [],
    });
  }, [values, isPending, skill]);

  const onChange = (values: Partial<SkillFormValues>) => {
    setValues((prev) => ({ ...prev, ...values }));
  };

  const onSubmit = () => {
    if (!values.name) {
      toast.error('Name is required');
      return;
    }

    mutate({
      name: values.name,
      description: values.description || null,
      category: values.category,
      action: values.action,
      issue_ids: values.issue_ids,
    });
  };

  const pestOptions = issues.map((issue) => ({
    value: issue.id,
    label: issue.name,
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showClose className='max-w-2xl' id='skills-dialog'>
        <DialogHeader>
          <DialogTitle>Edit Skill</DialogTitle>
          <DialogDescription>Edit the skill details.</DialogDescription>
        </DialogHeader>
        <SkillFormInputs className='py-4' values={values} onChange={onChange} pestOptions={pestOptions} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button type='submit' onClick={onSubmit} disabled={isDisabled}>
            {isPending ? 'Updating...' : 'Update'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface SkillFormValues {
  name: string;
  description: string;
  category: SkillCategory | null;
  action: SkillAction | null;
  issue_ids: string[];
}

interface SkillFormInputsProps {
  className?: string;
  values: SkillFormValues;
  onChange: (values: Partial<SkillFormValues>) => void;
  pestOptions: Array<{ value: string; label: string }>;
}

function SkillFormInputs({ className, values, onChange, pestOptions }: SkillFormInputsProps) {
  /**
   * Popover needs to be portalled into the dialog container for scroll inside of it to work.
   */
  const [dialogContainer, setDialogContainer] = useState<Element | null>(null);
  useEffect(() => {
    const container = document.getElementById('skills-dialog');
    if (container) {
      setDialogContainer(container);
    }
  }, []);

  return (
    <div className={cn('grid gap-4 py-4', className)}>
      <div className='grid w-full grid-cols-4 gap-2'>
        <Label htmlFor='name'>Name *</Label>
        <Input
          id='name'
          className='col-span-3 text-sm'
          value={values.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder='Enter skill name'
        />
      </div>
      <div className='grid w-full grid-cols-4 gap-2'>
        <Label htmlFor='category'>Category</Label>
        <div className='col-span-3 flex items-center gap-2'>
          <Select
            placeholder='Select category'
            options={SKILL_CATEGORY}
            value={values.category || ''}
            onChange={(v) => onChange({ category: v })}
            className='w-full'
          />
          {values.category && (
            <Button variant='outline' size='icon' onClick={() => onChange({ category: null })}>
              <XIcon className='size-4' />
            </Button>
          )}
        </div>
      </div>
      <div className='grid w-full grid-cols-4 gap-2'>
        <Label htmlFor='description' className='place-self-start pt-2'>
          Description
        </Label>
        <Textarea
          id='description'
          value={values.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder='Enter description (optional)'
          className='col-span-3 w-full text-sm'
        />
      </div>
      <Separator />
      <div className='grid w-full grid-cols-4 gap-2'>
        <Label htmlFor='action'>Action</Label>
        <div className='col-span-3 flex items-center gap-2'>
          <Select
            placeholder='Select action'
            options={SKILL_ACTION}
            value={values.action || ''}
            onChange={(v) => onChange({ action: v })}
            className='col-span-3 w-full'
          />
          {values.action && (
            <Button variant='outline' size='icon' onClick={() => onChange({ action: null })}>
              <XIcon className='size-4' />
            </Button>
          )}
        </div>
      </div>
      <div>
        <div className='flex w-full justify-between'>
          <Label>Pests</Label>
          <Popover
            trigger={
              <Button variant='outline' role='combobox' className='w-40 items-center gap-2'>
                <PlusCircleIcon className='size-4' />
                <span>Add Pests</span>
              </Button>
            }
            align='end'
            container={dialogContainer ?? undefined}
          >
            <MultiSelectContent
              selected={values.issue_ids}
              onChange={(pest_ids) => onChange({ issue_ids: pest_ids })}
              options={pestOptions}
            />
          </Popover>
        </div>
        {values.issue_ids.length > 0 && (
          <div className='flex flex-wrap gap-2'>
            {values.issue_ids.map((pest_id) => (
              <Badge key={pest_id} className='rounded-full' variant='secondary'>
                {pestOptions.find((pest) => pest.value === pest_id)?.label}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
